import { useCallback, useEffect, useRef, useState } from 'react';
import { STATE, SLIDES } from './SlideControls';
import { LearnhubReview } from '../types';
import useCoordinatedNavigationBlocker from '../hooks/useCoordinatedNavigationBlocker';

interface UseSlideEditReminderProps {
  reviewData?: LearnhubReview;
  onSave: () => void;
  onCompare: () => void;
  selectedSlide: string;
  setSelectedSlide: (slide: string) => void;
}

interface SlideEditReminderState {
  showModal: boolean;
  reviewState: string;
  pendingNavigation: (() => void) | null;
}

const getReviewState = (review?: LearnhubReview): string => {
  if (review) {
    if (review.savedSlide) {
      if (review.draftSlide) {
        return STATE.SAVED_COPY;
      }
      return STATE.SAVED;
    }

    if (review.draftSlide) {
      return STATE.DRAFT;
    }
  }

  return STATE.NO_COPIES;
};

const useSlideEditReminder = ({
  reviewData,
  onSave,
  onCompare,
  selectedSlide,
  setSelectedSlide,
}: UseSlideEditReminderProps) => {
  const { registerBlocker, unregisterBlocker, allowNavigation } =
    useCoordinatedNavigationBlocker();
  const [reminderState, setReminderState] = useState<SlideEditReminderState>({
    showModal: false,
    reviewState: STATE.NO_COPIES,
    pendingNavigation: null,
  });

  const reviewState = getReviewState(reviewData);
  const isNavigatingAwayRef = useRef(false);

  // Check if we should show reminder based on current state
  const shouldShowReminder = useCallback((): boolean => {
    // Scenario 1: User has created an editable copy but hasn't saved it
    if (reviewState === STATE.DRAFT) {
      return true;
    }

    // Scenario 2: User has saved a version but not selected it
    if (reviewState === STATE.SAVED && selectedSlide !== SLIDES.MY_SAVED_VERSION) {
      return true;
    }

    // Also handle SAVED_COPY state - user has saved version but is viewing draft
    if (reviewState === STATE.SAVED_COPY && selectedSlide !== SLIDES.MY_SAVED_VERSION) {
      return true;
    }
    return false;
  }, [reviewState, selectedSlide]);

  // Register with coordinated navigation blocker
  useEffect(() => {
    const blockerId = 'slide-edit-reminder';

    registerBlocker({
      id: blockerId,
      priority: 1, // Higher priority than lesson progress tracking
      shouldBlock: () => !isNavigatingAwayRef.current && shouldShowReminder(),
      onBlock: () => {
        isNavigatingAwayRef.current = true;
        setReminderState({
          showModal: true,
          reviewState,
          pendingNavigation: () => {
            allowNavigation();
          },
        });
        return { showModal: true };
      },
    });

    // Cleanup on unmount
    return () => {
      unregisterBlocker(blockerId);
    };
  }, [
    reviewState,
    selectedSlide,
    shouldShowReminder,
    registerBlocker,
    unregisterBlocker,
    allowNavigation,
  ]);

  // Modal action handlers
  const handleSave = () => {
    onSave();
    setReminderState((prev) => ({ ...prev, showModal: false }));
    if (reminderState.pendingNavigation) {
      reminderState.pendingNavigation();
    }
  };

  const handleCompare = () => {
    onCompare();
    setReminderState((prev) => ({ ...prev, showModal: false }));
    // Don't navigate away after compare - user stays on page
  };

  const handleSkip = () => {
    setReminderState((prev) => ({ ...prev, showModal: false }));
    if (reminderState.pendingNavigation) {
      reminderState.pendingNavigation();
    }
  };

  const handleTeachCurrent = () => {
    // Keep current selection
    setReminderState((prev) => ({ ...prev, showModal: false }));
    if (reminderState.pendingNavigation) {
      reminderState.pendingNavigation();
    }
  };

  const handleTeachNew = () => {
    // Switch to saved version
    setSelectedSlide(SLIDES.MY_SAVED_VERSION);
    setReminderState((prev) => ({ ...prev, showModal: false }));
    if (reminderState.pendingNavigation) {
      reminderState.pendingNavigation();
    }
  };

  const handleCancel = () => {
    isNavigatingAwayRef.current = false;
    setReminderState((prev) => ({ ...prev, showModal: false, pendingNavigation: null }));
  };

  return {
    showModal: reminderState.showModal,
    reviewState: reminderState.reviewState,
    handleSave,
    handleCompare,
    handleSkip,
    handleTeachCurrent,
    handleTeachNew,
    handleCancel,
  };
};

export default useSlideEditReminder;
